// src/services/taskService.js
/**
 * Task Service — data layer abstraction.
 *
 * All Redux thunks call this service, never localStorage directly.
 * To swap for a real API, replace each function body; the interface stays the same.
 *
 * Return shapes mirror what a REST API would return:
 *   { data, error }
 */

const STORAGE_KEY = 'taskflow_tasks';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(tasks) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch {
    // storage full or unavailable — fail silently in production
    console.warn('[TaskService] Could not persist tasks to localStorage');
  }
}

// Seed data for first run
const SEED_TASKS = [
  {
    id: 'task-seed-1',
    title: 'Build user authentication UI',
    description: 'Create login and signup forms with validation and error handling.',
    priority: 'high',
    assignee: 'Alice Chen',
    dueDate: (() => {
      const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().split('T')[0];
    })(),
    status: 'in_progress',
    createdAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
  {
    id: 'task-seed-2',
    title: 'Implement JWT authentication API',
    description: 'Build JWT token generation, refresh, and validation endpoints.',
    priority: 'critical',
    assignee: 'David Kim',
    dueDate: (() => {
      const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().split('T')[0];
    })(),
    status: 'in_progress',
    createdAt: new Date(Date.now() - 86400000 * 4).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
  {
    id: 'task-seed-3',
    title: 'Design board layout',
    description: 'Create task board layout with drag-and-drop functionality.',
    priority: 'high',
    assignee: 'Emma Wilson',
    dueDate: (() => {
      const d = new Date(); d.setDate(d.getDate() + 5); return d.toISOString().split('T')[0];
    })(),
    status: 'done',
    createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
  },
  {
    id: 'task-seed-4',
    title: 'Create REST API endpoints',
    description: 'Develop CRUD endpoints for tasks, users, and projects.',
    priority: 'high',
    assignee: 'Frank Lee',
    dueDate: (() => {
      const d = new Date(); d.setDate(d.getDate() + 4); return d.toISOString().split('T')[0];
    })(),
    status: 'backlog',
    createdAt: new Date(Date.now() - 86400000 * 6).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 6).toISOString(),
  },
  {
    id: 'task-seed-5',
    title: 'Setup database schema',
    description: 'Design and implement PostgreSQL schema for users, tasks, and projects.',
    priority: 'critical',
    assignee: 'Bob Martinez',
    dueDate: (() => {
      const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0];
    })(),
    status: 'backlog',
    createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 7).toISOString(),
  },
  {
    id: 'task-seed-6',
    title: 'Implement notification system',
    description: 'Create real-time notifications using WebSockets.',
    priority: 'medium',
    assignee: 'Carol Johnson',
    dueDate: '',
    status: 'review',
    createdAt: new Date(Date.now() - 86400000 * 10).toISOString(),
    updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
  },
];

export const taskService = {
  async fetchAll() {
    const stored = loadFromStorage();
    const data = stored ?? SEED_TASKS;
    if (!stored) saveToStorage(SEED_TASKS);
    return { data, error: null };
  },

  async create(taskData) {
    const stored = loadFromStorage() ?? [];
    const updated = [...stored, taskData];
    saveToStorage(updated);
    return { data: taskData, error: null };
  },

  async update(taskData) {
    const stored = loadFromStorage() ?? [];
    const updated = stored.map((t) => (t.id === taskData.id ? taskData : t));
    saveToStorage(updated);
    return { data: taskData, error: null };
  },

  async remove(taskId) {
    const stored = loadFromStorage() ?? [];
    const updated = stored.filter((t) => t.id !== taskId);
    saveToStorage(updated);
    return { data: taskId, error: null };
  },

  async updateStatus(taskId, status) {
    const stored = loadFromStorage() ?? [];
    const task = stored.find((t) => t.id === taskId);
    if (!task) return { data: null, error: 'Task not found' };
    const updated_task = { ...task, status, updatedAt: new Date().toISOString() };
    const updated = stored.map((t) => (t.id === taskId ? updated_task : t));
    saveToStorage(updated);
    return { data: updated_task, error: null };
  },
};
